import React, { useState } from 'react';
import styled from 'styled-components';
import * as TabilityTypes from 'types';
import { useInfiniteQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { useHistory } from 'react-router-dom';
import theme from 'theme';
import parse from 'parse-link-header';

// API
import * as remoteApi from 'api/remote';

// Components
import { CustomTermKey, translate } from 'utils/customTermUtils';
import { shallowEqual, useSelector } from 'react-redux';
import InitiativeRow from 'components/InitiativeRow';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import KoalaButton from 'koala/components/Button';
import KoalaLoader from 'koala/components/Loader';

export const BlockContent = styled.div`
  font-weight: 700;
  grid-area: content;
  background: transparent;
  margin: 0;
  height: 100%;

  display: flex;
  align-items: center;
`;

export const BlockMeta = styled.div`
  font-weight: 700;
  grid-area: meta;
  display: flex;
  align-items: center;
  height: 100%;
`;
const Container = styled.div`
  margin-top: ${theme.spacing.x1};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.x1};
  border-bottom: 0;
  font-weight: 800;
  font-size: 1rem;
  height: 3.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: ${theme.spacing.x1} ${theme.spacing.x2};
`;

const InitiativeList = styled.div`
  .item {
    border-top: 1px solid ${theme.colors.N10};
  }

  .initiative-owners {
    display: none;
  }
  .initiative-due-date {
    display: flex;
  }
`;

const LoadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem ${theme.spacing.x2};
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.x1} ${theme.spacing.x2};
`;

const getNextPage = (response: any) => {
  if (response && response.headers && response.headers.link) {
    const links = response.headers.link;
    const parsed = parse(links);
    if (parsed && parsed.next) {
      return parsed.next.page;
    }
  }
  return null;
};

interface Props {
  membership: TabilityTypes.Membership;
  startDate: Date | null;
  finishDate: Date | null;
}

function FilteredInitiativesList(props: Props) {
  // Query that fetches all the initiatives with pending checkins
  const history = useHistory();
  const currentWorkspace: TabilityTypes.Workspace = useSelector(
    (state: any) => state.session.currentWorkspace,
    shallowEqual,
  );
  const { membership, startDate, finishDate } = props;
  const { t } = useTranslation();

  const [ownerCount, setOwnerCount] = useState(0);
  const [contributingInitiativesCount, setContributingInitiativesCount] = useState(0);
  const [initiativesGroups, setInitiativesGroups] = useState<AxiosResponse[]>([]);
  const [contributingInitiativesGroups, setContributingInitiativesGroups] = useState<AxiosResponse[]>([]);

  const queryKey = [
    queryKeys.initiatives,
    'search',
    {
      workspaceSlug: currentWorkspace.slug,
      limit: 5,
      filter: {
        start_at: startDate,
        finish_at: finishDate,
        membership_id: membership.id,
      },
    },
  ];

  const { isLoading, isFetchingMore, fetchMore, canFetchMore } = useInfiniteQuery(
    queryKey,
    remoteApi.searchInitiatives,
    {
      getFetchMore: (lastGroup) => {
        return getNextPage(lastGroup);
      },
      onSuccess: (data: AxiosResponse[]) => {
        const resultsCount = parseInt(data[0].headers['x-total'] ?? '0');
        setOwnerCount(resultsCount);
        setInitiativesGroups(data);
      },
    },
  );

  const queryKeyContributing = [
    queryKeys.initiatives,
    'search',
    {
      workspaceSlug: currentWorkspace.slug,
      limit: 5,
      filter: {
        start_at: startDate,
        finish_at: finishDate,
        contributor_id: membership.id,
      },
    },
  ];

  const {
    isLoading: isLoadingContributing,
    isFetchingMore: isFetchingMoreContributing,
    fetchMore: fetchMoreContributing,
    canFetchMore: canFetchMoreContributing,
  } = useInfiniteQuery(queryKeyContributing, remoteApi.searchInitiatives, {
    getFetchMore: (lastGroup, allGroups) => {
      return getNextPage(lastGroup);
    },
    onSuccess: (data: AxiosResponse[]) => {
      const resultsCount = parseInt(data[0].headers['x-total'] ?? '0');
      setContributingInitiativesCount(resultsCount);
      setContributingInitiativesGroups(data);
    },
  });

  if (isLoading) {
    return (
      <EmptyState>
        <p className="subtle">
          <KoalaLoader />
        </p>
      </EmptyState>
    );
  }

  const intiativesLabel = translate(currentWorkspace, CustomTermKey.INITIATIVE, 2).toLowerCase();

  return (
    <Container>
      {ownerCount === 0 && contributingInitiativesCount === 0 && (
        <>
          <h4>{t('shared.noData')}</h4>
        </>
      )}

      {ownerCount > 0 && (
        <>
          <HeaderRow>{t('workspaceProfile.activeInitiatives', { initiatives: intiativesLabel })}</HeaderRow>
          <InitiativeList className="item-list">
            {initiativesGroups.map((group: AxiosResponse<TabilityTypes.Initiative[]>) =>
              group.data.map((initiative) => {
                const blockId = `initiative:${initiative.nano_slug}`;

                const showHashRoute = `#${blockId}:show`;
                const handleShowInitiative = (e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  history.push(showHashRoute);
                };
                return (
                  <InitiativeRow
                    key={initiative.id}
                    initiative={initiative}
                    handleClick={handleShowInitiative}
                    showMeta
                  />
                );
              }),
            )}
            {canFetchMore && (
              <LoadContainer>
                <KoalaButton
                  appearance="secondary"
                  onClick={() => fetchMore()}
                  loading={!!isFetchingMore || isLoading}
                  disabled={!!isFetchingMore || isLoading}
                >
                  {t(`shared.loadMore`)}
                </KoalaButton>
              </LoadContainer>
            )}
          </InitiativeList>
        </>
      )}

      {contributingInitiativesCount > 0 && (
        <>
          <HeaderRow>{t('workspaceProfile.contributing')}</HeaderRow>
          <InitiativeList className="item-list">
            {contributingInitiativesGroups.map((group: AxiosResponse<TabilityTypes.Initiative[]>) =>
              group.data.map((initiative) => {
                return <InitiativeRow key={initiative.id} initiative={initiative} showMeta />;
              }),
            )}
            {canFetchMoreContributing && (
              <LoadContainer>
                <KoalaButton
                  appearance="secondary"
                  onClick={() => fetchMoreContributing()}
                  loading={!!isFetchingMoreContributing || isLoadingContributing}
                  disabled={!!isFetchingMoreContributing || isLoadingContributing}
                >
                  {t(`shared.loadMore`)}
                </KoalaButton>
              </LoadContainer>
            )}
          </InitiativeList>
        </>
      )}
    </Container>
  );
}

export default FilteredInitiativesList;
